import { Tooltip, Typography, useTheme, Theme } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { FlattenedFinalResultRow } from './useFlattenedFinalResults';

/**
 * DataGridTooltipCell Component
 *
 * A reusable cell renderer that displays cell value in a Tooltip with Typography.
 * Used to render text cells with ellipsis truncation and hover tooltips.
 *
 * FEATURES:
 * - Shows full text in tooltip on hover
 * - Truncates text with ellipsis
 * - Consistent typography styling
 * - Prevents horizontal overflow
 *
 * @param params - GridRenderCellParams from DataGrid
 * @param theme - Material-UI theme object
 * @param notAvailable - Localized "N/A" text
 * @returns Cell component with tooltip
 */
const DataGridTooltipCell = (
  params: GridRenderCellParams<FlattenedFinalResultRow>,
  theme: Theme,
  notAvailable: string
) => (
  <Tooltip title={params.value || notAvailable} arrow placement="top">
    <Typography
      variant="body2"
      sx={{
        fontWeight: theme.typography.body2.fontWeight,
        color: 'text.primary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {params.value || notAvailable}
    </Typography>
  </Tooltip>
);

/**
 * Custom hook to generate DataGrid columns for final results
 *
 * This hook provides a curated set of columns for the confirmation/final results table.
 * It displays fewer columns than the full mapping results view to provide a clean,
 * summary-focused view.
 *
 * COLUMNS DISPLAYED:
 * - Type (Source/Mapped/Both) - 3x
 * - ID - 5x
 * - Name - 10x (largest)
 * - Code - 5x
 * - Vocabulary - 6x
 * - Standard - 3x
 * - Domain - 3x
 * - Class - 5x
 *
 * COLUMN WIDTH FORMULA:
 * - Total width: W = 40x (where x = W/40)
 * - Minimum table width: 1100px (based on 12" laptop with 1366x768 resolution)
 * - Minimum x: x_min = 1100/40 = 27.5px
 * - All minWidth values maintain the same proportions (10:6:5:5:5:3:3:3)
 *
 * FEATURES:
 * - Proportional column widths that scale with table width
 * - Maintains proportions even at minimum width (for 12" laptop screens)
 * - Tooltips for all cell values
 * - Consistent typography styling
 * - Memoized for performance
 *
 * USAGE:
 * ```tsx
 * const columns = useFinalResultsColumns();
 * <DataGrid columns={columns} rows={flatRows} ... />
 * ```
 */
export const useFinalResultsColumns = (): GridColDef<FlattenedFinalResultRow>[] => {
  const theme = useTheme();
  const intl = useIntl();
  const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });

  return useMemo(
    () => [
      {
        field: 'type',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.type' }),
        flex: 3,
        minWidth: 85, // 3 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
      {
        field: 'id',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.id' }),
        flex: 5,
        minWidth: 140, // 5 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.name' }),
        flex: 10,
        minWidth: 275, // 10 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.code' }),
        flex: 5,
        minWidth: 180, // Slightly wider to reduce code truncation
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
      {
        field: 'vocabulary',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.vocabulary' }),
        flex: 6,
        minWidth: 165, // 6 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
      {
        field: 'standard',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.standard' }),
        flex: 3,
        minWidth: 85, // 3 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          // Determine "Yes" or "No" based on row type and status
          let displayValue = 'No'; // Default to "No"

          if (params.row.type === 'Mapped' || params.row.type === 'Both') {
            // Mapped rows and Both rows (which are both Source and Mapped) should always be "Yes"
            displayValue = 'Yes';
          } else if (params.row.type === 'Source') {
            // Source rows should be "Yes" only if Status is "Already standard"
            if (
              params.row.status &&
              params.row.status.toString().toLowerCase() === 'already standard'
            ) {
              displayValue = 'Yes';
            } else {
              displayValue = 'No';
            }
          }

          return (
            <Tooltip title={displayValue} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayValue}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'domain',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.domain' }),
        flex: 3,
        minWidth: 85, // 3 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
      {
        field: 'class',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.class' }),
        flex: 5,
        minWidth: 140, // 5 × 27.5px (12" laptop: 1366x768 screen)
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => DataGridTooltipCell(params, theme, notAvailable),
      },
    ],
    [theme, intl, notAvailable]
  );
};
